import { baseApi } from './BaseService';
import type { MyselfResponse } from '../../shared/access/access';
import {
  CompanyRoles,
  GetAllUsersResponse,
  GetProfileByIdResponse,
  UpdateProfileByIdRequest,
} from '../responseTypes/profile';
import { DESC, PROFILES_SORTING_CREATION_DATE } from '../../shared/constants';
import { IntegrationBinding, IntegrationBindingTarget } from '../../shared/types/integrationConfiguration';

const baseUrl = 'admin/profiles';

export const profilesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllProfiles: build.query<
      GetAllUsersResponse,
      {
        limit: number;
        offset: number;
        sorting: string;
        order: string;
        query?: string;
      }
    >({
      query: ({
        limit = 20,
        offset = 0,
        sorting = PROFILES_SORTING_CREATION_DATE,
        order = DESC,
        query = '',
      }) => ({
        url: `${baseUrl}/paginated`,
        params: { limit, offset, sorting, order, query },
      }),
    }),
    getProfileById: build.query<GetProfileByIdResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `${baseUrl}/id/${profileId}`,
      }),
      providesTags: ['Profile'],
    }),
    getProfileAttachedRoles: build.query<
      { attachedRoles: IntegrationBinding[] },
      { profileId: string }
    >({
      query: ({ profileId }) => ({
        url: `admin/attached_roles/${profileId}/type/${IntegrationBindingTarget.PROFILE}`,
      }),
      // All three attached-role mutations already invalidate Profile.
      providesTags: ['Profile'],
    }),
    getCurrentUserProfile: build.query<MyselfResponse, void>({
      query: () => ({
        url: `${baseUrl}/myself`,
      }),
      providesTags: ['Profile'],
    }),
    updateProfileById: build.mutation<
      GetProfileByIdResponse,
      { profileId: string; data: UpdateProfileByIdRequest }
    >({
      query: ({ profileId, data }) => ({
        url: `${baseUrl}/${profileId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Profile'],
    }),
    inviteUser: build.mutation<
      void,
      {
        email: string;
        tariffPlanId: string;
        companyId?: string;
        companyRole?: CompanyRoles;
      }
    >({
      query: (data) => ({
        url: baseUrl,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});
